import Title_ru_RU_Class        from "@/core/misc/decorators/Title_ru_RU_Class";

import Core                    from "@/core/Core";
import ProductCategoriesService from "@/domains/ProductCategories/ProductCategoriesService";
import PaymentReasonBasis       from "@/domains/PaymentRequests/PaymentReasonBasis";
import Characteristic           from "@/domains/Characteristics/Characteristic";
import Unit                     from "@/domains/Units/Unit";
import BaseEntity               from "@/core/misc/BaseEntity";
import VatRate                  from "@/core/types/VatRate";
import Multiplier               from "@/core/types/Multiplier";

/** Категория товара */
@Title_ru_RU_Class('Категория товара')
class ProductCategory extends BaseEntity {
	isPublished = false;
	isLeaf: boolean;
	isBookmarked: boolean;
	isService = false;

	/**
	 * Допускает ли категория привязку товаров. У листа всегда true (выставляет бэк),
	 * у ветки включается свитчером, когда товары вешают прямо на неё
	 * (номенклатура без общих категорий-листьев).
	 * Реквизиты (НДС, отсрочка, наценки, единицы, характеристики) есть только при true.
	 */
	allowsProducts = false;

	name: string;

	parentId: number;
	private _parent: ProductCategory | null = null;

	/** Идентификаторы дочерних категорий */
	children: number[] = [];

	/** Рамки характеристик, применимые к товарам этой категории */
	characteristics: Characteristic[] = [];
	allowedUnits: Unit[] = [];

	/** Отсрочка платежа, дней */
	paymentDelay: number;

	/** Транспортная наценка множителем */
	priceModTransport: Multiplier;

	/** Наценка прибыли множителем */
	priceModProfit: Multiplier;

	/** Ставка НДС категории */
	vatRate: VatRate;

	externalId: string | null;

	paymentReasonBasis: PaymentReasonBasis | null = null;

	static Events = {
		AttachUnit:           'product-category:attach-unit',
		DetachUnit:           'product-category:detach-unit',
		AttachCharacteristic: 'product-category:attach-characteristic',
		DetachCharacteristic: 'product-category:detach-characteristic',
	} as const;

	static fromServer(data: any, category = new ProductCategory()): ProductCategory {
		super.fromServer(data, category);

		category.paymentReasonBasis = Core.utils.hydrate(data.paymentReasonBasis, PaymentReasonBasis.fromServer);
		category.characteristics    = Core.utils.hydrateArray(data.characteristics, Characteristic.fromServer);
		category.allowedUnits       = Core.utils.hydrateArray(data.allowedUnits, Unit.fromServer);

		/** Numeric-типы автоматом не подхватываются — гидрируем явно.
		 *  Порядок тот же, что в объявлении полей.
		 *
		 *  Наценки приходят ПРОЦЕНТАМИ (0, 1.9, 6), поэтому fromPercent, а не
		 *  fromServer: последний принял бы 1.9 за сам множитель, то есть за
		 *  наценку в 90%. Ставка НДС, наоборот, процентом и хранится. */
		category.priceModTransport = Core.utils.hydrate(data.priceModTransport, Multiplier.fromPercent)!;
		category.priceModProfit    = Core.utils.hydrate(data.priceModProfit, Multiplier.fromPercent)!;
		category.vatRate           = Core.utils.hydrate(data.vatRate, VatRate.fromServer)!;

		return category;
	}

	static getEmpty(): ProductCategory {
		const result = new this();

		result.isLeaf = false;

		return result;
	}

	static fromAnotherCategory(anotherCategory: ProductCategory): ProductCategory {
		const result = new this();

		/** Подрузамеваю что всегда будет листок, не ветка */
		result.isLeaf         = anotherCategory.isLeaf;
		result.allowsProducts = anotherCategory.allowsProducts;
		result.name           = anotherCategory.name;
		result.isPublished    = false;

		result.allowedUnits    = [...anotherCategory.allowedUnits];
		result.characteristics = [...anotherCategory.characteristics];

		result.parentId = anotherCategory.parentId;

		result.paymentDelay      = anotherCategory.paymentDelay;
		result.priceModProfit    = anotherCategory.priceModProfit;
		result.priceModTransport = anotherCategory.priceModTransport;
		result.vatRate           = anotherCategory.vatRate;
		result.externalId        = anotherCategory.externalId;
		result.isService         = anotherCategory.isService;

		return result;
	}

	static fromParentCategory(parent: ProductCategory): ProductCategory {
		const result = new this();

		result.parent = parent;

		return result;
	}

	get parent(): ProductCategory | null {
		if (this._parent !== null) {
			return this._parent;
		}
		else if (!this.parentId) {
			return null;
		}
		else {
			this._parent = ProductCategoriesService
				.getProductCategoriesMap()
				.get(this.parentId) ?? null;

			return this._parent;
		}
	}

	set parent(value: ProductCategory) {
		const categoriesMap = ProductCategoriesService.getProductCategoriesMap();

		this._parent  = categoriesMap.get(value.id);
		this.parentId = this._parent.id;

		/** Если мы создаём категорию "Товар", то у родительской категории должны быть потомки
		 * c isLeaf: true (они должны быть листьями), в таком случае можно точно сказать, что сейчас
		 * мы создаём "Товар" с isLeaf: true */
		if (
      Core.utils.isNotEmptyArray(this._parent.children)
			&& categoriesMap.get(this._parent.children[0])?.isLeaf
		) {
			this.isLeaf = categoriesMap.get(this._parent.children[0])?.isLeaf;
		}

		/** В случае если категория была только что создана, то у неё не будет ни единого потомка,
		 * а это значит нужно проверять родителя, и если parent носит root.id, то создаваемый потомок
		 * это isLeaf
		 *
		 * upd: кстати rootCategories было бы классно вынести куда-то также как и getCategoriesMap
		 * */
		const rootCategories: number[] = [];

		categoriesMap.forEach((_value, key) => {
			if (_value.parentId === null) {
				rootCategories.push(key);
			}
		});

		if (
			this._parent.parentId &&
			rootCategories.includes(this._parent.parentId)
		) {
			this.isLeaf = true;
		}
	}

	getBookmarkIcon(): string {
		if (this.isBookmarked) {
			return 'favorite';
		}
		else {
			return 'favorite_border';
		}
	}

	attachUnit(unit: Unit): void {
		this.allowedUnits.push(unit);
	}

	detachUnit(unit: Unit): void {
		this.allowedUnits = this.allowedUnits.filter(_unit => _unit.id !== unit.id);
	}

	// Вывожу name, потому что в VSelectV2 я вызываю метода .toString()
	toString(): string {
		return this.name;
	}
}

export default ProductCategory;